/**
 * Copyright (c) 2023, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * WSO2 LLC. licenses this file to you under the Apache License,
 * Version 2.0 (the "License"); you may not use this file except
 * in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { NavigateBefore } from "@mui/icons-material";
import { Box, Container, Grid, Link, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import HistoryPagination from "../Components/History/HistoryPagination";
import Loader from "../Components/Loader/Loader";
import SubscribeButtonPopper from "../Components/SubscribeButtonPopper";
import { useCloud } from "../Hooks/Cloud";
import { useShowRegion } from "../Hooks/ShowRegion";

function History() {
  const { cloud } = useCloud();
  const { setIsVisible: setRegionPickerVisible } = useShowRegion();
  const [ incidentHistory, setIncidentHistory ] = useState({});
  const [ isLoadingHistory, setIsLoadingHistory ] = useState(true);
  const features = window.ENV[cloud].features;
  const views = window.ENV[cloud].views;

  const fetchIncidentHistory = () => {
    fetch(`${window.ENV.server.endpoint}/api/v1/incidents?cloud=${cloud}`)
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        }

        throw new Error(response.status);
      })
      .then((data) => {
        setIncidentHistory(data);
        setIsLoadingHistory(false);
      })
      .catch(() => {
        // If the request fails, try again after 5 seconds
        setTimeout(fetchIncidentHistory, 5000);
      });
  };

  useEffect(() => {
    setRegionPickerVisible(false);

    document.title = window.ENV[cloud].title + " - Incident History";
    document.querySelector("meta[name=\"description\"]").setAttribute("content",
      "Explore the historical status updates for our services.");

    fetchIncidentHistory();
  }, [ cloud ]);

  if (isLoadingHistory) {
    return <Loader />;
  }

  return (
    <Container maxWidth="md">
      <Container sx={ { mb: 4 } }>
        <HistoryPagination data={ incidentHistory } />
      </Container>
      <Container>
        <Grid container sx={ { alignItems: "center", display: "flex" } } >
          <Grid item rowSpacing={ 2 } xs={ 12 } md sm lg>
            <Box
              sx={ {
                alignItems: "center",
                display: "flex",
                justifyContent: "flex-start"

              } }
            >
              <Link href={ "/" } variant="body2" underline="none">
                <Typography component={ "span" } variant="inherit">
                  <Box component="span" sx={ { alignItems: "center", display: "flex" } }>
                    <NavigateBefore sx={ { pr: 1 } } />
                    <Box component="span">
                      Current Status
                    </Box>
                  </Box>
                </Typography>
              </Link>
            </Box>
          </Grid>
          { (features.subscribe && views?.showSubscribeFeedInHistory) && (
            <Grid item rowSpacing={ 2 }>
              <SubscribeButtonPopper />
            </Grid>
          ) }
        </Grid>
      </Container>
    </Container>
  );
}

export default History;
